/**
 * Session cache of list results per filter combination (Bookings list).
 *
 * Switching back to a recently used filter/event shows the cached rows
 * instantly while the list re-fetches page 1 in the background
 * (stale-while-revalidate). Only the last MAX_ENTRIES combinations are kept.
 */
interface ListResultEntry {
  rows: any[];
  nextToken: string | null | undefined;
  savedAt: number;
}

const MAX_ENTRIES = 5;
const MAX_AGE_MS = 30 * 60 * 1000;

const entries = new Map<string, ListResultEntry>();

export const saveListResult = (
  key: string,
  rows: any[],
  nextToken: string | null | undefined,
) => {
  if (!key || !Array.isArray(rows)) return;
  entries.delete(key); // re-insert = most recently used
  entries.set(key, { rows, nextToken, savedAt: Date.now() });
  while (entries.size > MAX_ENTRIES) {
    const oldest = entries.keys().next().value;
    if (oldest === undefined) break;
    entries.delete(oldest);
  }
};

export const getListResult = (key: string): ListResultEntry | null => {
  const entry = entries.get(key);
  if (!entry) return null;
  if (Date.now() - entry.savedAt > MAX_AGE_MS) {
    entries.delete(key);
    return null;
  }
  return entry;
};

export const clearListResults = () => entries.clear();

/**
 * Merge freshly updated bookings into every cached result, so reopening a cached
 * filter never shows the status from before an approve/reject.
 */
export const patchListResultRows = (updatedRows: any[]) => {
  const byId = new Map<string, any>(
    (updatedRows || []).filter((r) => r?.id).map((r) => [r.id, r]),
  );
  if (!byId.size) return;
  entries.forEach((entry) => {
    if (!entry.rows.some((row) => byId.has(row?.id))) return;
    entry.rows = entry.rows.map((row) => {
      const updated = byId.get(row?.id);
      return updated ? { ...row, ...updated } : row;
    });
  });
};
